import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId, accessToken) => {
    try {
      // Try via backend (more reliable, uses service role)
      if (accessToken) {
        const res = await fetch(`${API_URL}/api/profile`, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
        if (res.ok) {
          const result = await res.json();
          if (result.profile) {
            setProfile(result.profile);
            return result.profile;
          }
        }
      }

      // Fallback: direct Supabase
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) {
        setProfile(data);
        return data;
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
    }
    return null;
  };

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;

        if (session?.user) {
          setUser(session.user);
          await fetchProfile(session.user.id, session.access_token);
        }
      } catch (err) {
        console.error('Auth init error:', err);
      }
      if (mounted) setLoading(false);
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!mounted) return;
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchProfile(session.user.id, session.access_token);
        } else {
          setProfile(null);
        }
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email, password, role, displayName, companyName, orgInviteToken) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;

    if (data.user) {
      const body = {
        user_id: data.user.id,
        email,
        display_name: displayName,
        role,
      };
      if (companyName) body.company_name = companyName;
      if (orgInviteToken) body.org_invite_token = orgInviteToken;

      const res = await fetch(`${API_URL}/api/auth/create-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to create profile');
    }
    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const subscriptionTier = profile?.subscription_tier || null;
  // [EVENT-MODE] hasSubscription is now based on event_access_granted from admin
  const eventAccessGranted = !!profile?.event_access_granted;
  const hasSubscription = eventAccessGranted || ['inventor_49', 'studio_149', 'studio_member', 'company_349', 'enterprise'].includes(subscriptionTier) || !!profile?.org?.stripe_subscription_id;
  const isStudioOwner = subscriptionTier === 'studio_149';
  const isStudioMember = subscriptionTier === 'studio_member';
  const isStudio = isStudioOwner || isStudioMember;
  const studioOwnerId = profile?.studio_owner_id || (isStudioOwner ? profile?.id : null);
  const org = profile?.org || null;
  const orgId = profile?.org_id || null;
  const orgRole = profile?.org_role || null;
  const isOrgAdmin = orgRole === 'admin';

  // [EVENT-MODE] Event settings from backend
  const eventSettings = profile?.event_settings || null;
  const now = new Date();
  const eventStarted = eventSettings?.event_start ? new Date(eventSettings.event_start) <= now : false;
  const eventEnded = eventSettings?.event_end ? new Date(eventSettings.event_end) < now : false;
  const eventActive = eventStarted && !eventEnded;
  const submissionsOpen = !!eventSettings?.submissions_open;

  const activeRegs = (profile?.event_registrations || []).filter(r =>
    r.payment_status === 'paid' && new Date(r.access_expires_at) > new Date()
  );

  const hasEventAccess = eventAccessGranted || activeRegs.length > 0;

  const activeEventRegistration = activeRegs
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null;

  const hasEventAccessForId = (eventId) => {
    return activeRegs.some(r => r.event_id === eventId);
  };

  const getAccessToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  };

  const value = {
    user,
    profile,
    role: profile?.role,
    tier: subscriptionTier,
    subscriptionTier,
    loading,
    hasSubscription,
    eventAccessGranted,
    isStudioOwner,
    isStudioMember,
    isStudio,
    studioOwnerId,
    hasEventAccess,
    hasEventAccessForId,
    activeEventRegistration,
    // [EVENT-MODE] Event window state
    eventSettings,
    eventActive,
    eventStarted,
    eventEnded,
    submissionsOpen,
    org,
    orgId,
    orgRole,
    isOrgAdmin,
    getAccessToken,
    signUp,
    signIn,
    signOut,
    refreshProfile: async () => {
      if (user) {
        const { data: { session } } = await supabase.auth.getSession();
        await fetchProfile(user.id, session?.access_token);
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
